import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Activity, ArrowRightLeft, BadgeDollarSign, Bot, Box, FileText, Landmark, LayoutDashboard, Mail, Network, Settings, ShieldCheck, ShipWheel, Users, Workflow } from "lucide-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";

import { loginRequest, apiTokenRequest } from "./authConfig";

import Topbar, { type TopbarLink } from "./components/Topbar";

import { portalConfig } from "./config/portalConfig";
import { portalPages, PortalPageDefinition } from "./config/portalPages";
import RuntimePortalPage from "./pages/RuntimePortalPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import HsCountriesPage from './pages/HsCountriesPage';
import { getInternalAccess } from "./features/internal-access/api";
import { isPageAllowed } from "./features/internal-access/accessRules";
import { toAdminApiPath } from "./features/internal-access/adminApiPath";
import type { InternalAccess } from "./features/internal-access/types";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "";

if (!API_BASE && process.env.NODE_ENV !== "development") {
  throw new Error("REACT_APP_API_BASE_URL ontbreekt");
}

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

const pageIcons: Record<PortalPageDefinition["key"], React.ReactNode> = {
  landscape: <LayoutDashboard className="h-3.5 w-3.5" />,
  flowBuilder: <Workflow className="h-3.5 w-3.5" />,
  workCenter: <Box className="h-3.5 w-3.5" />,
  carrierIntegrations: <Network className="h-3.5 w-3.5" />,
  createPortal: <Settings className="h-3.5 w-3.5" />,
  logicAppMonitor: <Activity className="h-3.5 w-3.5" />,
  import: <ShipWheel className="h-3.5 w-3.5" />,
  mailRules: <Mail className="h-3.5 w-3.5" />,
  importAutomation: <Bot className="h-3.5 w-3.5" />,
  adminSettings: <Settings className="h-3.5 w-3.5" />,
  comboKeyChecker: <ShieldCheck className="h-3.5 w-3.5" />,
  comboKeyApprovals: <ShieldCheck className="h-3.5 w-3.5" />,
  internalUserManagement: <Users className="h-3.5 w-3.5" />,
  financeIntegrations: <ArrowRightLeft className="h-3.5 w-3.5" />,
  integrationCenter: <Network className="h-3.5 w-3.5" />,
  salesQuoteAutomation: <FileText className="h-3.5 w-3.5" />,
  agenciesInkoopInvoices: <FileText className="h-3.5 w-3.5" />,
};

function SignInGate({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  const loginBusy = inProgress !== InteractionStatus.None;

  if (!isAuthenticated) {
    return (
      <div className="grid min-h-screen place-items-center bg-white p-6 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <button
          disabled={loginBusy}
          onClick={() => {
            if (loginBusy) return;
            instance.loginRedirect(loginRequest);
          }}
          className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-950/30 transition hover:bg-orange-400 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {loginBusy ? "Inloggen..." : "Inloggen met Azure"}
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

function AppShell() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0] || null;
  const accountId = account?.homeAccountId || "";

  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "dark";

    return (
      (window.localStorage.getItem("portalTheme") as "light" | "dark") ||
      "dark"
    );
  });

  const [internalAccess, setInternalAccess] = useState<InternalAccess | null>(null);
  const [accessCheckDone, setAccessCheckDone] = useState(false);
  const [accessCheckFailed, setAccessCheckFailed] = useState(false);
  const [accessFailureDetail, setAccessFailureDetail] = useState("");

  useEffect(() => {
    document.title = "Jordex Portal";
  }, []);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated || !account) {
      throw new Error("Niet ingelogd");
    }

    try {
      const resp = await instance.acquireTokenSilent({
        account,
        scopes: apiTokenRequest.scopes,
      });

      return resp.accessToken;
    } catch (err) {
      if (
        err instanceof InteractionRequiredAuthError &&
        inProgress === InteractionStatus.None
      ) {
        await instance.acquireTokenRedirect({
          account,
          scopes: apiTokenRequest.scopes,
        });
      }

      throw new Error("Authenticatie mislukt");
    }
  }, [isAuthenticated, account, instance, inProgress]);

  const apiFetch = useCallback<ApiFetch>(
    async (path: string, init: RequestInit = {}) => {
      const token = await getAccessToken();

      const headers = new Headers(init.headers || {});

      headers.set("Authorization", `Bearer ${token}`);

      if (!(init.body instanceof FormData) && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }

      return fetch(`${API_BASE}${toAdminApiPath(path)}`, {
        ...init,
        headers,
      });
    },
    [getAccessToken]
  );

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !accountId || inProgress !== InteractionStatus.None) {
      return () => {
        cancelled = true;
      };
    }

    setAccessCheckDone(false);
    setAccessCheckFailed(false);
    setAccessFailureDetail("");
    setInternalAccess(null);

    getInternalAccess(apiFetch)
      .then((access) => {
        if (!cancelled) setInternalAccess(access);
      })
      .catch((error) => {
        if (!cancelled) {
          setAccessFailureDetail(error instanceof Error ? error.message : "Onbekende fout");
          setAccessCheckFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setAccessCheckDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accountId, apiFetch, inProgress]);

  const visiblePages = useMemo<PortalPageDefinition[]>(() => {
    const enabledPages = portalConfig.enabledPages as string[];

    return portalPages.filter((page: PortalPageDefinition) =>
      enabledPages.includes(page.key)
    );
  }, []);

  const allowedPages = useMemo<PortalPageDefinition[]>(() => {
    if (!internalAccess) return [];

    return visiblePages.filter((page) => isPageAllowed(page, internalAccess));
  }, [visiblePages, internalAccess]);

  const isPortalAdmin = internalAccess?.isSuperadmin || internalAccess?.role === "admin";

  const links = useMemo<TopbarLink[]>(() => {
    const groups: Array<{
      label: string;
      icon: React.ReactNode;
    }> = [
      {
        label: "IT",
        icon: <LayoutDashboard className="h-3.5 w-3.5" />,
      },
      {
        label: "Finance",
        icon: <Landmark className="h-3.5 w-3.5" />,
      },
      {
        label: "Sales",
        icon: <BadgeDollarSign className="h-3.5 w-3.5" />,
      },
      {
        label: "Operations",
        icon: <Settings className="h-3.5 w-3.5" />,
      },
      {
        label: "Import",
        icon: <ShipWheel className="h-3.5 w-3.5" />,
      },
      {
        label: "Export",
        icon: <Network className="h-3.5 w-3.5" />,
      },
    ];

    return groups.map(({ label, icon }) => ({
      label,
      icon,
      children: allowedPages.filter((page) => page.group === label && page.showInMenu).map((page) => ({
        label: page.label, to: page.path.replace(/\/\*$/, ""), icon: pageIcons[page.key], sectionLabel: page.menuGroup,
      })),
    })).filter((group) => group.children.length > 0);
  }, [allowedPages]);

  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    window.localStorage.setItem("portalTheme", theme);
  }, [theme]);

  const defaultRoute = allowedPages[0]?.path.replace(/\/\*$/, "") || "/";
  const routablePages = visiblePages;
  const hasInternalPortalAccess = internalAccess?.isSuperadmin || internalAccess?.portalAccess;

  return (
    <div className="it-portal min-h-screen bg-white text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100">
      <Topbar
        title=""
        links={links}
        theme={theme}
        userName={account?.name || account?.username}
        userEmail={account?.username}
        userRole={internalAccess?.isSuperadmin ? "Superadmin" : isPortalAdmin ? "Portal admin" : "Portal user"}
        onThemeToggle={() =>
          setTheme((current) =>
            current === "dark" ? "light" : "dark"
          )
        }
        minimalChrome
        showUserSummary={false}
        showThemeToggle={false}
        adminSettingsTo={isPortalAdmin ? "/admin-settings" : undefined}
        onLogout={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
      />

      <main className="min-h-[calc(100vh-64px)] bg-slate-50/70 px-4 py-6 sm:px-8 lg:px-10 lg:py-8 dark:bg-slate-950">
        <div className="mx-auto w-full max-w-[1600px]">
          <Routes>
            <Route path="/hs-landen" element={!accessCheckDone ? <div className="it-surface p-5">Laden…</div> : accessCheckFailed ? <AccessDeniedPage reason="checkFailed" detail={accessFailureDetail}/> : !hasInternalPortalAccess ? <AccessDeniedPage reason="portal"/> : internalAccess && isPortalAdmin && isPageAllowed({key:'comboKeyApprovals'},internalAccess) ? <HsCountriesPage apiFetch={apiFetch}/> : <AccessDeniedPage reason="page"/>}/>
            {routablePages.map((page: PortalPageDefinition) => {
              const PageComponent =
                page.component as React.ComponentType<any>;

              const renderedPage = (
                <PageComponent
                  apiFetch={apiFetch}
                  canReviewComboKeys={internalAccess
                    ? isPageAllowed({ key: "comboKeyApprovals" }, internalAccess)
                    : false}
                />
              );

              return (
                <Route
                  key={page.key}
                  path={page.path}
                  element={
                    !accessCheckDone ? (
                      <div className="it-surface p-5 text-slate-500 dark:text-slate-400">
                        Laden…
                      </div>
                    ) : accessCheckFailed ? (
                      <AccessDeniedPage reason="checkFailed" detail={accessFailureDetail} />
                    ) : !hasInternalPortalAccess ? (
                      <AccessDeniedPage reason="portal" />
                    ) : page.path === "/" && defaultRoute !== "/" ? (
                      <Navigate to={defaultRoute} replace />
                    ) : internalAccess && isPageAllowed(page, internalAccess) ? (
                      renderedPage
                    ) : (
                      <AccessDeniedPage reason="page" />
                    )
                  }
                />
              );
            })}

            <Route
              path="/portals/:portalKey"
              element={
                !accessCheckDone ? (
                  <div className="it-surface p-5 text-slate-500 dark:text-slate-400">Laden…</div>
                ) : accessCheckFailed ? (
                  <AccessDeniedPage reason="checkFailed" detail={accessFailureDetail} />
                ) : !hasInternalPortalAccess ? (
                  <AccessDeniedPage reason="portal" />
                ) : internalAccess && isPageAllowed({ key: "createPortal" }, internalAccess) ? (
                  <RuntimePortalPage apiFetch={apiFetch} />
                ) : (
                  <AccessDeniedPage reason="page" />
                )
              }
            />

            <Route
              path="/portal/:portalKey"
              element={
                !accessCheckDone ? (
                  <div className="it-surface p-5 text-slate-500 dark:text-slate-400">Laden…</div>
                ) : accessCheckFailed ? (
                  <AccessDeniedPage reason="checkFailed" detail={accessFailureDetail} />
                ) : !hasInternalPortalAccess ? (
                  <AccessDeniedPage reason="portal" />
                ) : internalAccess && isPageAllowed({ key: "createPortal" }, internalAccess) ? (
                  <RuntimePortalPage apiFetch={apiFetch} />
                ) : (
                  <AccessDeniedPage reason="page" />
                )
              }
            />

            <Route
              path="*"
              element={<Navigate to={defaultRoute} replace />}
            />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <SignInGate>
        <AppShell />
      </SignInGate>
    </Router>
  );
}
